import React, { useState, useEffect } from 'react';
import { 
  Card, Row, Col, Statistic, Alert, Table, Spin, Tabs, Tag, Space, Typography, Descriptions 
} from 'antd';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { readingService } from '../../services/api/reading.service';
import type { ReadingAnomaly, ReadingStats as Rs } from '../../types/reading';
import { ArrowDownOutlined, ArrowUpOutlined, DashboardOutlined, WarningOutlined, DollarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import ReadingStats from 'components/readings/ReadingStats';
 
const { TabPane } = Tabs;
const { Title, Text } = Typography;

const UserDashboard: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<Rs | null>(null);
  const [anomalies, setAnomalies] = useState<ReadingAnomaly[]>([]);
  const [latestReading, setLatestReading] = useState<any>(null);
  const [consumptionAnalysis, setConsumptionAnalysis] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (user) {
      fetchDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsResponse, anomaliesResponse, latestResponse, analysisResponse] = await Promise.all([
        readingService.getStatistics(user?.water_meter?.id),
        readingService.getAnomalies(user?.water_meter?.id),
        readingService.getLatestReading(),
        readingService.getConsumptionAnalysis(user?.water_meter?.id)
      ]);

      setStats(statsResponse.data);
      setAnomalies(anomaliesResponse.data.anomalies);
      setLatestReading(latestResponse.data);
      setConsumptionAnalysis(analysisResponse.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  const anomalyColumns = [
    {
      title: 'Datum',
      dataIndex: 'reading_date',
      key: 'reading_date',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY')
    },
    {
      title: 'Potrošnja',
      dataIndex: 'consumption',
      key: 'consumption',
      render: (value: number) => `${value.toFixed(2)} m³`
    },
    {
      title: 'Dnevni prosjek',
      dataIndex: 'daily_consumption',
      key: 'daily_consumption',
      render: (value: number) => `${value.toFixed(2)} m³/dan`
    },
    {
      title: 'Odstupanje',
      dataIndex: 'difference_percentage',
      key: 'difference_percentage',
      render: (value: number) => `${value.toFixed(1)}%`
    }
  ];

  return (
    <div className="p-6">
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Pregled" key="overview">
          <h1 className="text-2xl font-bold mb-6">Korisnički panel</h1>
          
          {/* Water Meter Info */}
          <Card className="mb-6">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <div>
                  <h3 className="font-semibold">Broj vodomjera</h3>
                  <p>{user?.water_meter?.meter_id}</p>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div>
                  <h3 className="font-semibold">Adresa</h3>
                  <p>{user?.water_meter?.address}</p>
                </div>
              </Col>
              {consumptionAnalysis?.category_info && (
                <Col xs={24} sm={12} md={8}>
                  <div>
                    <h3 className="font-semibold">Kategorija</h3>
                    <p>{consumptionAnalysis.category_info.kategorija_naziv || 'Standardna'}</p>
                  </div>
                </Col>
              )}
            </Row>
          </Card>

          {/* Latest Reading Alert */}
          {latestReading && (
            <Alert
              message={`Zadnje očitavanje: ${latestReading.reading_value} m³ (${dayjs(latestReading.reading_date).format('DD.MM.YYYY')})`}
              type="info"
              showIcon
              className="mb-6"
            />
          )}

          {/* Statistics */}
          <Row gutter={[16, 16]} className="mb-6">
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Stanje"
                  value={latestReading?.reading_value || 0}
                  precision={2}
                  suffix="m³"
                  prefix={<DashboardOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Zaduženje"
                  value={latestReading?.zaduzenje || consumptionAnalysis?.financial_summary?.total_zaduzenje || 0}
                  precision={2}
                  suffix="KM"
                  prefix={<DollarOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Procenat"
                  value={latestReading?.procenat || 0}
                  precision={2}
                  suffix="%"
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Anomalije"
                  value={anomalies.length}
                  prefix={<WarningOutlined />}
                  valueStyle={{ color: anomalies.length > 0 ? '#faad14' : 'inherit' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Financial Data */}
          {consumptionAnalysis?.financial_summary && (
            <Card title="Finansijski pregled" className="mb-6">
              <Descriptions bordered column={{ xxl: 4, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }}>
                <Descriptions.Item label="Početno stanje">
                  {parseFloat(consumptionAnalysis.financial_summary.total_pocetno_stanje).toFixed(2)} KM
                </Descriptions.Item>
                <Descriptions.Item label="Ukupno zaduženje">
                  {parseFloat(consumptionAnalysis.financial_summary.total_zaduzenje).toFixed(2)} KM
                </Descriptions.Item>
                <Descriptions.Item label="Uplate">
                  {parseFloat(consumptionAnalysis.financial_summary.total_uplata).toFixed(2)} KM
                </Descriptions.Item>
                <Descriptions.Item label="Saldo">
                  <Text type={parseFloat(consumptionAnalysis.financial_summary.total_ukupno) < 0 ? 'danger' : 'success'}>
                    {parseFloat(consumptionAnalysis.financial_summary.total_ukupno).toFixed(2)} KM
                  </Text>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          {/* Category Info */}
          {consumptionAnalysis?.category_info && (
            <Card title="Informacije o kategoriji" className="mb-6">
              <Descriptions bordered column={{ xxl: 4, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }}>
                <Descriptions.Item label="Kategorija">
                  {consumptionAnalysis.category_info.kategorija_naziv || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Prioritet">
                  {consumptionAnalysis.category_info.prioritet_naziv || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Broj članova">
                  {consumptionAnalysis.category_info.broj_clanova || '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          {/* Consumption Charts */}
          <ReadingStats 
            userId={user?.id} 
            waterMeterId={user?.water_meter?.id} 
          />

          {/* Anomalies Table */}
          {anomalies.length > 0 && (
            <Card title="Detektovane anomalije" className="mt-6">
              <Alert 
                message="Upozorenje o neuobičajenoj potrošnji"
                description="Detektovane su značajne promjene u potrošnji vode koje mogu ukazivati na curenje ili kvar."
                type="warning"
                showIcon
                className="mb-4"
              />
              <Table
                columns={anomalyColumns}
                dataSource={anomalies}
                pagination={false}
                rowKey="reading_date"
              />
            </Card>
          )}
        </TabPane>

        <TabPane tab="Historija potrošnje" key="consumption">
          {consumptionAnalysis?.consumption_analysis && (
            <Card title="Detaljna historija potrošnje">
              <Table
                dataSource={consumptionAnalysis.consumption_analysis}
                rowKey={(record, index) => `${index}`}
                pagination={false}
                columns={[
                  {
                    title: 'Od',
                    dataIndex: 'period_start',
                    key: 'period_start',
                    render: (date: string) => dayjs(date).format('DD.MM.YYYY')
                  },
                  {
                    title: 'Do',
                    dataIndex: 'period_end',
                    key: 'period_end',
                    render: (date: string) => dayjs(date).format('DD.MM.YYYY')
                  },
                  {
                    title: 'Potrošnja (m³)',
                    dataIndex: 'consumption',
                    key: 'consumption',
                    render: (val: number) => val.toFixed(2)
                  },
                  {
                    title: 'Dani',
                    dataIndex: 'days',
                    key: 'days'
                  },
                  {
                    title: 'Dnevna potrošnja (m³)',
                    dataIndex: 'daily_average',
                    key: 'daily_average',
                    render: (val: number) => val.toFixed(2)
                  },
                  {
                    title: 'Procenat',
                    dataIndex: 'procenat',
                    key: 'procenat',
                    render: (val: number) => val ? `${val.toFixed(2)}%` : '-'
                  },
                  {
                    title: 'Zaduženje (KM)',
                    dataIndex: 'zaduzenje',
                    key: 'zaduzenje',
                    render: (val: number) => val ? val.toFixed(2) : '-'
                  }
                ]}
              />
            </Card>
          )}
        </TabPane>
      </Tabs>
    </div>
  );
};

export default UserDashboard;